import {
  Building2,
  CheckCircle2,
  FileHeart,
  Stethoscope,
  UserCog,
  ShieldAlert,
  Trash2,
  Users,
} from "lucide-react";
import { Link } from "react-router";
import { Card, CardContent } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { ErroConsulta } from "@/components/ErroConsulta";

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Users;
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <Card className="border-admin-200">
      <CardContent className="flex items-center gap-4 p-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs uppercase tracking-wider text-admin-700/60">{label}</p>
          <p className="text-2xl font-bold text-admin-900">{value}</p>
          {hint && <p className="mt-0.5 truncate text-xs text-admin-700">{hint}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminOverview() {
  const { data, isLoading, error } = trpc.admin.stats.overview.useQuery();
  const pendentes = trpc.admin.lgpd.pendentes.useQuery();
  const exclusoes = trpc.admin.exclusoes.quantas.useQuery();

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-900 border-t-transparent" />
      </div>
    );
  }
  if (error) return <ErroConsulta error={error} />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Visao geral</h1>
        <p className="mt-1 text-sm text-admin-700">Estado atual da plataforma em numeros.</p>
      </div>

      {!!pendentes.data && (
        <Link to="/lgpd" className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 hover:bg-amber-100">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span>
            <b>{pendentes.data}</b> {pendentes.data > 1 ? "solicitações LGPD pendentes" : "solicitação LGPD pendente"} de reset ou exclusão.
          </span>
        </Link>
      )}

      {!!exclusoes.data && (
        <Link to="/exclusoes" className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 hover:bg-red-100">
          <Trash2 className="h-4 w-4 shrink-0" />
          <span>
            <b>{exclusoes.data}</b> {exclusoes.data > 1 ? "organizações com exclusão agendada" : "organização com exclusão agendada"}.
          </span>
        </Link>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard icon={Users} label="Usuarios" value={data.totalUsers} />
        <StatCard
          icon={UserCog}
          label="Administradores"
          value={data.totalAdmins}
          hint="platformRole = ADMIN"
        />
        <StatCard
          icon={CheckCircle2}
          label="Onboarded"
          value={data.totalOnboarded}
          hint={`${Math.round((data.totalOnboarded / Math.max(data.totalUsers, 1)) * 100)}% do total`}
        />
        <StatCard icon={Building2} label="Clinicas" value={data.totalOrganizations} />
        <StatCard icon={Stethoscope} label="Pacientes" value={data.totalPatients} />
        <StatCard icon={FileHeart} label="Estudos DICOM" value={data.totalStudies} />
      </div>
    </div>
  );
}
